// ScopesInfo.tsx
import React, { useCallback, useMemo } from "react";
import { Checkbox, FormControlLabel, Grid } from "@material-ui/core";
import { Field } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { AppState } from "../../../store";
import { createAgentsStyles } from "../create/create-agents.styles";
import { setTheAgentForm } from "../store/agent-form.slice";
import useAuthority from "app/hooks/useAuthority";
import { ROLES } from "app/constants/roles";

type AgentFormShape = {
  scopes?: string[];
  [key: string]: any;
};

type ScopeItem = {
  value: string;
  labelKey: string;
  defaultLabel: string;
  adminOnly?: boolean;
};

const SCOPE_ITEMS: ScopeItem[] = [
  {
    value: "FLIGHT_RESERVATION",
    labelKey: "app.main.agency.create.scopesInfo.label1",
    defaultLabel: "Booking flights",
  },
  {
    value: "ADD_TRAVELER",
    labelKey: "app.main.agency.create.scopesInfo.label2",
    defaultLabel: "Addition of passengers",
  },
  {
    value: "DEACTIVATE_TRAVELER",
    labelKey: "app.main.agency.create.scopesInfo.label3",
    defaultLabel: "Cancellation of passengers",
  },
  {
    value: "CANCELLATION_OF_RESERVATION",
    labelKey: "app.main.agency.create.scopesInfo.label15",
    defaultLabel: "Cancellation of reservation",
  },
  {
    value: "RESERVATION_DISCUSSION",
    labelKey: "app.main.agency.create.scopesInfo.label5",
    defaultLabel: "Booking note",
  },
  {
    value: "UPDATE_TRAVELERS",
    labelKey: "app.main.agency.create.scopesInfo.label6",
    defaultLabel: "Change of passengers",
  },
  {
    value: "CONFIRM_VIBER",
    labelKey: "app.main.agency.create.scopesInfo.label4",
    defaultLabel: "Viber confirmation",
  },
  {
    value: "ACTIVITIES",
    labelKey: "app.main.agency.create.scopesInfo.label8",
    defaultLabel: "Activities",
  },
  {
    value: "ADD_DISCUSSION",
    labelKey: "app.main.agency.create.scopesInfo.label9",
    defaultLabel: "Adding remarks",
  },
  {
    value: "UPDATE_TRAVELER_PRICES",
    labelKey: "app.main.agency.create.scopesInfo.label10",
    defaultLabel: "Passenger price change",
  },
  {
    value: "SHOW_TRAVELERS",
    labelKey: "app.main.agency.create.scopesInfo.label11",
    defaultLabel: "Show/Don't show passengers",
  },
  {
    value: "UPDATE_CONTACT",
    labelKey: "app.main.agency.create.scopesInfo.label12",
    defaultLabel: "Changing contactor",
  },
  {
    value: "DELETE_TRAVELERS",
    labelKey: "app.main.agency.create.scopesInfo.label13",
    defaultLabel: "Deleting passengers",
  },
  {
    value: "UPDATE_TRAVELER_FLIGHTS",
    labelKey: "app.main.agency.create.scopesInfo.label14",
    defaultLabel: "Change of passenger flights",
  },
  {
    value: "ALLOW_CREATING_SUB_AGENTS",
    labelKey: "app.main.agency.create.scopesInfo.label16",
    defaultLabel: "Allow creating subagents",
    adminOnly: true,
  },
];

const ScopesInfo = () => {
  const dispatch = useDispatch();
  const classes = createAgentsStyles();
  const { t } = useTranslation();

  const { hasUserAnyAuthority } = useAuthority();
  const isAdmin = hasUserAnyAuthority([ROLES.ROLE_ADMIN]);

  const agentForm = useSelector(
    (state: AppState) => state.agents.agentForm
  ) as AgentFormShape;

  const scopes: string[] = Array.isArray(agentForm?.scopes)
    ? agentForm.scopes
    : [];

  const visibleScopeItems = useMemo(() => {
    return SCOPE_ITEMS.filter((scope) => {
      if (scope.adminOnly && !isAdmin) {
        return false;
      }

      return true;
    });
  }, [isAdmin]);

  const visibleScopeValues = useMemo(() => {
    return visibleScopeItems.map((scope) => scope.value);
  }, [visibleScopeItems]);

  const selectedVisibleScopesCount = visibleScopeValues.filter((scope) =>
    scopes.includes(scope)
  ).length;

  const isAllSelected =
    visibleScopeValues.length > 0 &&
    selectedVisibleScopesCount === visibleScopeValues.length;

  const isPartiallySelected =
    selectedVisibleScopesCount > 0 &&
    selectedVisibleScopesCount < visibleScopeValues.length;

  const updateScopes = useCallback(
    (nextScopes: string[]) => {
      dispatch(
        setTheAgentForm({
          ...(agentForm ?? {}),
          scopes: nextScopes,
        })
      );
    },
    [dispatch, agentForm]
  );

  const handleCheckbox = useCallback(
    (value: string) => {
      const nextScopes = scopes.includes(value)
        ? scopes.filter((item) => item !== value)
        : [...scopes, value];

      updateScopes(nextScopes);
    },
    [scopes, updateScopes]
  );

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      /**
       * Remove only the privileges from this visible section.
       * This is safer if some hidden/system scopes exist in the form.
       */
      const nextScopes = scopes.filter(
        (scope) => !visibleScopeValues.includes(scope)
      );

      updateScopes(nextScopes);
      return;
    }

    /**
     * Add all visible privileges and avoid duplicates.
     */
    const nextScopes = Array.from(
      new Set([...scopes, ...visibleScopeValues])
    );

    updateScopes(nextScopes);
  }, [isAllSelected, scopes, visibleScopeValues, updateScopes]);

  const renderScopeCheckbox = (scope: ScopeItem) => {
    return (
      <label key={scope.value}>
        <Field
          type="checkbox"
          name={scope.value}
          checked={scopes.includes(scope.value)}
          value={scope.value}
          onChange={() => handleCheckbox(scope.value)}
        />
        {t(scope.labelKey, scope.defaultLabel)}
      </label>
    );
  };

  return (
    <div style={{ marginBottom: "10px" }}>
      <div
        style={{
          backgroundColor: "lightgrey",
          padding: "15px",
          marginLeft: "-16px",
          marginRight: "-16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 className={classes.title} style={{ margin: 0 }}>
          {t(
            "app.main.agency.agentsList.agentEdit.sidebar.title2",
            "Agent privileges"
          )}
        </h2>

        <FormControlLabel
          control={
            <Checkbox
              checked={isAllSelected}
              indeterminate={isPartiallySelected}
              onChange={handleSelectAll}
              color="primary"
            />
          }
          label={t("app.main.agency.create.scopesInfo.selectAll", "Select All")}
        />
      </div>

      <Grid container spacing={4} className={classes.permissions}>
        <Grid item xs={3}>
          <div className={classes.checkboxGroup} role="group">
            {visibleScopeItems.slice(0, 4).map(renderScopeCheckbox)}
          </div>
        </Grid>

        <Grid item xs={3}>
          <div className={classes.checkboxGroup} role="group">
            {visibleScopeItems.slice(4, 8).map(renderScopeCheckbox)}
          </div>
        </Grid>

        <Grid item xs={3}>
          <div className={classes.checkboxGroup} role="group">
            {visibleScopeItems.slice(8, 11).map(renderScopeCheckbox)}
          </div>
        </Grid>

        <Grid item xs={3}>
          <div className={classes.checkboxGroup} role="group">
            {visibleScopeItems.slice(11).map(renderScopeCheckbox)}
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default ScopesInfo;